import {useMemo} from 'react';
import {Formik} from 'formik';
import * as Yup from 'yup';
import {useTranslation} from 'react-i18next';
import {useSelector} from 'react-redux';
import PhoneInput from '../../../../../component/input/phoneInput/PhoneInput';
import SuccessCheckMark from '../../../../../assets/icons/successCheckMark';
import PhoneHandset from '../../../../../assets/icons/PhoneHandset';
import {TOrderChangePhonePayload} from '../../../../../redux/crm/crm.type';
import {TState} from '../../../../../redux/store.type';
import * as sharedCss from '../../OrderChangePhonePopup.css';
import * as css from './PhoneFormStep.css';
import {
    PhoneFormValues, PhoneChangeMode,
    computePhoneFormatOk, computeTenDigitsOk, computeDiffersFromCurrentOk,
} from '../../utils';
import OrderMetaRow from '../OrderMetaRow/OrderMetaRow';
import InfoBanner from '../InfoBanner/InfoBanner';

type Props = {
    orderData: TOrderChangePhonePayload;
    phoneChangeMode: PhoneChangeMode;
    onSubmit: (values: PhoneFormValues) => Promise<void>;
};

function ValidationItem({isOk, label}: {isOk: boolean; label: string}) {
    const {t} = useTranslation('layout');
    return (
        <css.validationItem>
            {isOk ? <SuccessCheckMark size={24} color='var(--primitive-green-600)' backgroundColor='var(--primitive-green-400)' /> : <css.checkOff />}
            <css.validationLabel>{t(label)}</css.validationLabel>
        </css.validationItem>
    );
}

export default function PhoneFormStep({orderData, phoneChangeMode, onSubmit}: Props) {
    const {t} = useTranslation('layout');
    const {t: tLogin} = useTranslation('login');

    const langloc = useSelector((s: TState) => s.user.langloc);
    const defaultCountry = langloc?.split('_')?.[1]?.toUpperCase() ?? 'IL';

    const initialValues = useMemo((): PhoneFormValues => ({phone: '', country: defaultCountry}), [defaultCountry]);

    const validationSchema = useMemo(() => Yup.object().shape({
        phone: Yup.string().required()
            .test('phone-format', tLogin('numberMessage'), function(value) {
                return value ? computePhoneFormatOk(value, this.parent.country) : false;
            })
            .test('ten-digits', t('orderChangePhoneCheckTenDigits'), function(value) {
                return value ? computeTenDigitsOk(value, this.parent.country) : false;
            })
            .test('not-same', t('orderChangePhoneSameAsCurrent'), function(value) {
                return value ? computeDiffersFromCurrentOk(value, this.parent.country, orderData?.phone) : false;
            }),
        country: Yup.string().required(),
    }), [tLogin, t, orderData]);

    return (
        <Formik initialValues={initialValues} validationSchema={validationSchema} onSubmit={onSubmit} enableReinitialize>
            {({handleSubmit, values}) => {
                const formatOk = computePhoneFormatOk(values.phone, values.country);
                const tenDigitsOk = computeTenDigitsOk(values.phone, values.country);
                const differsOk = computeDiffersFromCurrentOk(values.phone, values.country, orderData?.phone);
                return (
                    <form id="phone-form" onSubmit={handleSubmit}>
                        <sharedCss.card>
                            <sharedCss.dealTitle>{t('orderChangePhoneDealLabel', {orderId: orderData.oid})}</sharedCss.dealTitle>
                            <OrderMetaRow orderData={orderData} />
                            <sharedCss.divider />
                            <css.phoneInputTitle>{t('orderChangePhoneNewNumberLabel')}</css.phoneInputTitle>
                            <PhoneInput countryFieldName="country" name="phone" text={t('orderChangePhoneNewNumberLabel')} events={{}} className="" icon={<PhoneHandset size={24} />} />
                            <css.validationRow>
                                <ValidationItem isOk={tenDigitsOk} label="orderChangePhoneCheckTenDigits" />
                                <ValidationItem isOk={differsOk} label="orderChangePhoneCheckDiffers" />
                                <ValidationItem isOk={formatOk} label="orderChangePhoneCheckFormat" />
                            </css.validationRow>
                            <InfoBanner mode={phoneChangeMode} phone={values.phone} />
                        </sharedCss.card>
                    </form>
                );
            }}
        </Formik>
    );
}
